import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { ArrowLeft, AlertTriangle, Clock, TrendingDown, CheckCircle2, Search, RefreshCw, Pill, Package, ChevronDown, ChevronRight, Lock } from 'lucide-react';
import { EditLotExpiration } from '../components/EditLotExpiration';
import { toast } from 'sonner';
import { fmtDate as fmtDateTz } from '../lib/datetime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (d) => fmtDateTz(d);

const STATUS_META = {
  expired:     { label: 'Vencido',          color: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300',     icon: AlertTriangle },
  expiring_30: { label: 'Vence pronto',     color: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300', icon: Clock },
  low_stock:   { label: 'Bajo reposición',  color: 'bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300', icon: TrendingDown },
  normal:      { label: 'OK',               color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300', icon: CheckCircle2 },
};

export default function WarehouseStock() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useOutletContext();
  const isAdmin = user?.role === 'admin';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [search, setSearch] = useState('');
  const [detailItem, setDetailItem] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const toggleExpand = (id) => setExpandedId(prev => prev === id ? null : id);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (categoryFilter) params.append('category', categoryFilter);
      const url = `${BACKEND_URL}/api/warehouses/${id}/stock${params.toString() ? '?' + params.toString() : ''}`;
      const r = await axios.get(url, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cargar el stock');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id, categoryFilter]); // eslint-disable-line

  const categories = useMemo(() => {
    if (!data?.items) return [];
    return Array.from(new Set(data.items.map(i => i.category).filter(Boolean))).sort();
  }, [data]);

  const visibleItems = useMemo(() => {
    if (!data?.items) return [];
    return data.items.filter(i => {
      if (statusFilter !== 'all' && i.status !== statusFilter) return false;
      if (search && !((i.name || '').toLowerCase().includes(search.toLowerCase()))) return false;
      return true;
    });
  }, [data, statusFilter, search]);

  const s = data?.summary || { total: 0, expired: 0, expiring_30: 0, low_stock: 0 };

  return (
    <div className="p-6 space-y-6" data-testid="warehouse-stock-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/warehouses')} data-testid="back-to-warehouses">
            <ArrowLeft className="w-4 h-4 mr-1" /> Depósitos
          </Button>
          <h1 className="text-3xl font-bold tracking-tight mt-2">
            {data?.warehouse?.name || 'Depósito'}
          </h1>
          {data?.warehouse?.location && (
            <p className="text-muted-foreground text-sm">{data.warehouse.location}</p>
          )}
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="refresh-stock">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refrescar
        </Button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Total artículos" value={s.total} active={statusFilter === 'all'} onClick={() => setStatusFilter('all')} color="bg-card" testId="stat-total" />
        <StatCard label="Vencidos" value={s.expired} active={statusFilter === 'expired'} onClick={() => setStatusFilter('expired')} color="bg-red-50 dark:bg-red-950/30 border-red-300/50" icon={<AlertTriangle className="w-4 h-4 text-red-600" />} testId="stat-expired" />
        <StatCard label="Vencen ≤30 días" value={s.expiring_30} active={statusFilter === 'expiring_30'} onClick={() => setStatusFilter('expiring_30')} color="bg-amber-50 dark:bg-amber-950/30 border-amber-300/50" icon={<Clock className="w-4 h-4 text-amber-600" />} testId="stat-expiring" />
        <StatCard label="Bajo reposición" value={s.low_stock} active={statusFilter === 'low_stock'} onClick={() => setStatusFilter('low_stock')} color="bg-orange-50 dark:bg-orange-950/30 border-orange-300/50" icon={<TrendingDown className="w-4 h-4 text-orange-600" />} testId="stat-low" />
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-6 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <Label>Buscar artículo</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-2.5 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nombre…"
                className="pl-8"
                data-testid="search-input"
              />
            </div>
          </div>
          <div>
            <Label>Categoría</Label>
            <select
              className="h-9 rounded-md border border-input bg-background px-3 text-sm min-w-[180px]"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              data-testid="category-filter"
            >
              <option value="">(Todas)</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          {(statusFilter !== 'all' || categoryFilter || search) && (
            <Button variant="ghost" size="sm" onClick={() => { setStatusFilter('all'); setCategoryFilter(''); setSearch(''); }} data-testid="clear-filters">
              Limpiar filtros
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card>
        <CardHeader>
          <CardTitle>Artículos en stock</CardTitle>
          <CardDescription>{visibleItems.length} resultado(s)</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <p className="text-muted-foreground p-6">Cargando…</p>
          ) : visibleItems.length === 0 ? (
            <p className="text-muted-foreground p-6">No hay artículos con esos filtros.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>Artículo</TableHead>
                    <TableHead>Categoría</TableHead>
                    <TableHead className="text-right">Stock</TableHead>
                    <TableHead className="text-right">Pto. Rep.</TableHead>
                    <TableHead className="text-right">Lotes</TableHead>
                    <TableHead>Próx. Vto.</TableHead>
                    <TableHead>Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleItems.map(it => {
                    const meta = STATUS_META[it.status] || STATUS_META.normal;
                    const Icon = meta.icon;
                    const isExpanded = expandedId === it.article_id;
                    const hasLots = (it.lots || []).length > 0;
                    return (
                      <React.Fragment key={it.article_id}>
                        <TableRow
                          className="cursor-pointer hover:bg-accent"
                          onClick={() => hasLots ? toggleExpand(it.article_id) : setDetailItem(it)}
                          data-testid={`stock-row-${it.article_id}`}
                        >
                          <TableCell>
                            {hasLots && (isExpanded
                              ? <ChevronDown className="w-4 h-4 text-muted-foreground" />
                              : <ChevronRight className="w-4 h-4 text-muted-foreground" />)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {it.is_medicamento ? <Pill className="w-4 h-4 text-violet-600" /> : <Package className="w-4 h-4 text-blue-600" />}
                              <span className="font-medium">{it.name}</span>
                              {it.is_archived && (
                                <span className="text-xs font-semibold text-amber-600" data-testid={`archived-label-${it.article_id}`}>(SIN REPOSICION-UTILIZAR HASTA AGOTAR)</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground">{it.category || '—'}</TableCell>
                          <TableCell className="text-right tabular-nums font-semibold">{it.stock} <span className="text-xs font-normal text-muted-foreground">{it.unit || 'unidad'}</span></TableCell>
                          <TableCell className="text-right tabular-nums text-muted-foreground">{it.reorder_point || '—'}</TableCell>
                          <TableCell className="text-right tabular-nums">{it.lots_count}</TableCell>
                          <TableCell className="text-xs">
                            {it.next_expiration ? (
                              <span>
                                {fmtDate(it.next_expiration)}{' '}
                                <span className="text-muted-foreground">
                                  ({it.days_to_expiration < 0 ? `vencido hace ${-it.days_to_expiration}d` : `en ${it.days_to_expiration}d`})
                                </span>
                              </span>
                            ) : '—'}
                          </TableCell>
                          <TableCell>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${meta.color}`}>
                              <Icon className="w-3 h-3" />
                              {meta.label}
                            </span>
                          </TableCell>
                        </TableRow>
                        {isExpanded && hasLots && (
                          <TableRow className="bg-muted/30 hover:bg-muted/30" data-testid={`lots-detail-${it.article_id}`}>
                            <TableCell colSpan={8} className="py-2">
                              <LotsDetail lots={it.lots} isAdmin={isAdmin} onSaved={load} />
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!detailItem} onOpenChange={(open) => !open && setDetailItem(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {detailItem?.is_medicamento ? <Pill className="w-5 h-5 text-violet-600" /> : <Package className="w-5 h-5 text-blue-600" />}
              {detailItem?.name}
            </DialogTitle>
            <DialogDescription>
              {detailItem?.category || 'Sin categoría'} · {detailItem?.lots_count || 0} lote(s) activos · Stock: {detailItem?.stock}
            </DialogDescription>
          </DialogHeader>
          {detailItem?.lots?.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Lote</TableHead>
                    <TableHead className="text-right">Cantidad</TableHead>
                    <TableHead>Vencimiento</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detailItem.lots.map(l => (
                    <TableRow key={l.lot_id}>
                      <TableCell className="font-mono text-sm">{l.lot_number || '—'}</TableCell>
                      <TableCell className="text-right tabular-nums">{l.quantity_remaining}</TableCell>
                      <TableCell className="text-sm">
                        {l.expiration_date ? fmtDate(l.expiration_date) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Sin lotes registrados (artículo sin trazabilidad por lote).</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Label({ children }) {
  return <div className="text-xs font-medium text-muted-foreground mb-1">{children}</div>;
}

function LotsDetail({ lots, isAdmin, onSaved }) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const sorted = [...lots].sort((a, b) => {
    const da = a.expiration_date ? new Date(a.expiration_date).getTime() : Infinity;
    const dbb = b.expiration_date ? new Date(b.expiration_date).getTime() : Infinity;
    return da - dbb;
  });
  return (
    <div className="pl-8 pr-4">
      <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">Lotes que componen el stock</div>
      <div className="grid grid-cols-12 text-[11px] font-semibold text-muted-foreground border-b pb-1 mb-1 uppercase gap-x-4">
        <span className="col-span-3">Lote</span>
        <span className="col-span-2 text-right">Cantidad</span>
        <span className="col-span-3">Vencimiento</span>
        <span className="col-span-2 text-right">Días</span>
        <span className="col-span-2">Origen</span>
      </div>
      {sorted.map(l => {
        const exp = l.expiration_date ? new Date(l.expiration_date) : null;
        if (exp) exp.setHours(0, 0, 0, 0);
        const days = exp ? Math.round((exp - today) / 86400000) : null;
        const tone = days === null ? '' : (days < 0 ? 'text-red-600 font-semibold' : days <= 30 ? 'text-amber-600 font-medium' : days <= 90 ? 'text-amber-500' : '');
        return (
          <div key={l.lot_id} className="grid grid-cols-12 text-xs py-1 border-b last:border-0 gap-x-4">
            <span className="col-span-3 font-mono">{l.lot_number || '—'}</span>
            <span className="col-span-2 text-right tabular-nums font-semibold">{l.quantity_remaining}</span>
            <span className={`col-span-3 flex items-center gap-1 ${tone}`}>
              {fmtDate(l.expiration_date)}
              {isAdmin && <EditLotExpiration lot={l} onSaved={onSaved} />}
            </span>
            <span className={`col-span-2 text-right tabular-nums ${tone}`}>
              {days === null ? '—' : days < 0 ? `vencido hace ${-days}d` : `${days}d`}
            </span>
            <span className="col-span-2">
              {l.quarantine
                ? <span className="inline-flex items-center gap-1 text-red-700 dark:text-red-400" title={`Cuarentena: ${l.nci_condition_label || l.nci_condition || 'NCI'}`}>🚫 {l.nci_condition_label || 'Cuarentena'}</span>
                : l.from_return
                ? <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400" title={`Devolución NCI ${l.from_return}`}>↩️ Devolución</span>
                : l.external_intake
                ? <span className="inline-flex items-center gap-1 text-rose-700"><Lock className="w-3 h-3" />REM · {l.patient_name || ''}</span>
                : l.reception_number
                ? <span className="font-mono text-blue-700 dark:text-blue-400" title={`Lote ingresado por la recepción ${l.reception_number}`}>{l.reception_number}</span>
                : l.source === 'import_excel'
                ? <span className="text-muted-foreground italic" title="Carga inicial del sistema (importación masiva)">Imp. Masiva</span>
                : <span className="text-muted-foreground">Compras</span>}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function StatCard({ label, value, active, onClick, color, icon, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`text-left p-4 rounded-lg border transition ${color} ${active ? 'ring-2 ring-primary' : 'hover:shadow'}`}
    >
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">{label}</div>
        {icon}
      </div>
      <div className="text-2xl font-bold mt-1 tabular-nums">{value}</div>
    </button>
  );
}
